import { useQuery } from "@apollo/client/react";
import { HiCollection } from "react-icons/hi";
import {
  CardDifficultyDistributionDocument,
  CardPerformanceDocument,
  CardStateTimelineDocument,
} from "../../../generated/graphql";
import type { FilterState } from "../../../pages/fsrs/AnalyticsDashboardPage";
import { CardDifficultyChart } from "../charts/CardDifficultyChart";
import { CardPerformanceTable } from "../charts/CardPerformanceTable";
import { CardStateTimelineChart } from "../charts/CardStateTimelineChart";

interface CardsTabProps {
  filters: FilterState;
}

export function CardsTab({ filters }: CardsTabProps) {
  const { selectedStudentId, selectedLessonId } = filters;

  const { data: performanceData, loading: performanceLoading } = useQuery(CardPerformanceDocument, {
    variables: {
      studentId: selectedStudentId || "",
      lessonId: selectedLessonId || null,
      sortBy: "difficulty",
      sortOrder: "desc",
      limit: 50,
      offset: 0,
    },
    skip: !selectedStudentId,
  });

  const { data: distributionData, loading: distributionLoading } = useQuery(
    CardDifficultyDistributionDocument,
    {
      variables: {
        studentId: selectedStudentId || "",
        lessonId: selectedLessonId || null,
      },
      skip: !selectedStudentId,
    }
  );

  const { data: timelineData, loading: timelineLoading } = useQuery(CardStateTimelineDocument, {
    variables: {
      studentId: selectedStudentId || "",
      dateRange: null,
    },
    skip: !selectedStudentId,
  });

  // Empty state if no student selected
  if (!selectedStudentId) {
    return (
      <div className="flex items-center justify-center h-96">
        <div className="text-center">
          <HiCollection className="text-6xl mb-4 mx-auto text-gray-600" />
          <h3 className="h5 mb-2">Select a Student</h3>
          <p className="text-gray-600">
            Choose a student from the filters above to view card analytics
          </p>
        </div>
      </div>
    );
  }

  const isLoading = performanceLoading || distributionLoading || timelineLoading;

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-96">
        <div className="text-gray-600">Loading card analytics...</div>
      </div>
    );
  }

  return (
    <div className="space-y-8">
      {/* Header */}
      <div>
        <h2 className="h4">Card-Level Analytics</h2>
        <p className="text-gray-600 mt-1">
          Individual card performance, difficulty distribution, and state transitions
        </p>
      </div>

      {/* Section 1: Difficulty Distribution */}
      <section>
        <h3 className="h5 mb-4">Difficulty Distribution</h3>
        <div className="panel p-6">
          <CardDifficultyChart data={distributionData?.cardDifficultyDistribution || []} />
        </div>
      </section>

      {/* Section 2: Card State Timeline */}
      <section>
        <h3 className="h5 mb-4">Card State Timeline</h3>
        <div className="panel p-6">
          <CardStateTimelineChart data={timelineData?.cardStateTimeline || []} />
        </div>
      </section>

      {/* Section 3: Card Performance Table */}
      <section>
        <h3 className="h5 mb-4">Individual Card Performance</h3>
        <CardPerformanceTable cards={performanceData?.cardPerformance || []} />
      </section>
    </div>
  );
}
